import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { AppointmentResponse } from 'api/api.types'
import { APIService } from 'api/APIService'
import { compressImage } from 'utils/compressImage'
import { usePopulatedAppointments } from 'src/hooks/usePopulatedAppointments'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { ShadowSelect } from 'components/FormElements/ShadowSelect/ShadowSelect'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { Loader } from 'components/Utility/Loader/Loader'
import { StarsWithLabel } from 'components/Utility/Stars/StarsWithLabel'
import { DateInfo } from 'components/Profile/VisitInfo/DateInfo'
import { SalonInfo } from 'components/Profile/VisitInfo/SalonInfo'
import formStyles from './ReviewForm.module.scss'
import styles from '../Form.module.scss'

const MAX_PHOTOS = 3
/** Larger photos are re-encoded before upload */
const COMPRESS_FROM_BYTES = 5000000

const QUESTIONS = [
  'Качество оказанной услуги',
  'Время обслуживания',
  'Чистота в салоне',
  'Атмосфера в салоне',
  'Общее впечатление от посещения салона',
]

const readAsDataUrl = (blob: Blob) => new Promise<string>((resolve) => {
  const reader = new FileReader()
  reader.onloadend = () => resolve(reader.result as string)
  reader.readAsDataURL(blob)
})

/** Asks for image files with the browser's file picker */
const pickImages = () => new Promise<File[]>((resolve) => {
  const input = document.createElement('input')
  input.type = 'file'
  input.multiple = true
  input.accept = 'image/*'
  input.addEventListener('change', () => resolve(Array.from(input.files ?? [])))
  input.click()
})

type ReviewFormProps = {
  onSuccess: () => void,
}

/** #review: a general review, or ratings for a recent visit, with up to three photos */
export const ReviewForm: React.FC<ReviewFormProps> = ({ onSuccess }) => {
  const [answers, setAnswers] = useState<Record<string, number>>(
    () => Object.fromEntries(QUESTIONS.map((_, i) => [`answer${i + 1}`, 5])),
  )
  const [comment, setComment] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [visits, setVisits] = useState<AppointmentResponse[]>([])
  const [photosPreviews, setPhotosPreviews] = useState<string[]>([])
  const [loadingPreviews, setLoadingPreviews] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [formSuccess, setFormSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [visitMode, setVisitMode] = useState(false)
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null)
  const populatedVisits = usePopulatedAppointments(visits)

  useEffect(() => {
    APIService.profile.fetchReviewableVisits().then(setVisits)
  }, [])

  useEffect(() => {
    setLoadingPreviews(true)
    Promise.all(photos.map(readAsDataUrl)).then((previews) => {
      setPhotosPreviews(previews)
      setLoadingPreviews(false)
    })
  }, [photos])

  const handleSubmit = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!comment) {
      setFormError('Не указан комментарий')
      return
    }
    setLoading(true)
    try {
      if (visitMode) {
        await APIService.profile.sendReview(selectedVisitId as number, answers, comment, photos)
      } else {
        await APIService.profile.sendAbstractReview(comment, photos)
      }
      setFormSuccess(true)
    } catch (err) {
      setFormError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [comment, visitMode, selectedVisitId, answers, photos])

  const handleAddPhotoClick = useCallback(async () => {
    if (photos.length >= MAX_PHOTOS) {
      return
    }
    const files = (await pickImages()).slice(0, MAX_PHOTOS - photos.length)
    const prepared = await Promise.all(files.map((file) => (
      file.size < COMPRESS_FROM_BYTES ? file : compressImage(file, { quality: 0.5 })
    )))
    setPhotos((current) => [...current, ...prepared])
  }, [photos.length])

  const removePhoto = useCallback((index: number) => {
    setPhotos((current) => current.filter((_, i) => i !== index))
  }, [])

  const dismissSuccess = useCallback(() => {
    setFormSuccess(false)
    onSuccess()
  }, [onSuccess])

  const options = useMemo(() => populatedVisits.map((visit) => ({
    id: visit.id,
    name: <div className={formStyles.visitInfo}>
      <DateInfo className={formStyles.dateInfo} date={visit.date} time={visit.time} />
      <SalonInfo className={formStyles.salonInfo} salon={visit.salon} />
    </div>,
  })), [populatedVisits])

  const hasVisitsForReview = populatedVisits.length > 0

  return (
    <div className={styles.form}>
      <div className={styles.fields}>
        {hasVisitsForReview && <div className={styles.fieldFullWidth}>
          <input
            className={styles.checkbox}
            type={'checkbox'}
            id={'about-visit'}
            name={'personal-data'}
            checked={visitMode}
            onChange={(e) => setVisitMode(e.target.checked)}
          />
          <label className={styles.checkboxLabel} htmlFor={'about-visit'}>
            <span>О визите</span>
          </label>
        </div>}
        {visitMode && hasVisitsForReview && <div className={styles.fieldFullWidth}>
          <ShadowSelect
            value={selectedVisitId ?? ''}
            placeholder={'Выберите визит'}
            onChange={setSelectedVisitId}
            options={options}
          />
          {QUESTIONS.map((title, i) => {
            const name = `answer${i + 1}`
            return (
              <StarsWithLabel
                key={name}
                title={title}
                name={name}
                value={answers[name]}
                onChange={(value) => setAnswers((current) => ({ ...current, [name]: value }))}
                className={formStyles.stars}
                titleClassName={i > 0 ? formStyles.starsTitle : undefined}
              />
            )
          })}
        </div>}
        <div className={styles.fieldFullWidth}>
          <textarea
            onChange={(e) => setComment(e.target.value)}
            value={comment}
            placeholder={'Комментарий'}
            className={formStyles.textarea}
          />
        </div>
      </div>
      {(photosPreviews.length > 0 || loadingPreviews) && (
        <div className={formStyles.previews}>
          {loadingPreviews && <Loader />}
          {photosPreviews.map((preview, index) => (
            <div className={formStyles.preview} key={index}>
              <i className={formStyles.removePhotoIcon} onClick={() => removePhoto(index)} />
              <img src={preview} alt={'icon'} />
            </div>
          ))}
        </div>
      )}
      <div className={styles.actions}>
        <AppButton onClick={handleAddPhotoClick} className={styles.action} disabled={photos.length >= MAX_PHOTOS}>
          Добавить фото
        </AppButton>
        <AppButton onClick={handleSubmit} className={styles.action} disabled={loading}>
          Оставить отзыв
        </AppButton>
      </div>
      <StatusModal
        status={'error'}
        modalText={formError}
        buttonText={'ОК'}
        onClose={() => setFormError(null)}
        onButtonClick={() => setFormError(null)}
        isOpen={formError !== null}
      />
      <StatusModal
        status={'success'}
        modalText={'Отзыв успешно отправлен'}
        buttonText={'ОК'}
        onClose={dismissSuccess}
        onButtonClick={dismissSuccess}
        isOpen={formSuccess}
      />
    </div>
  )
}
